// fileUtils.js
// File and image utilities
import * as XLSX from "xlsx";

import { getDateTimeString } from "./formatUtils";
import { debugLog, errorLog } from "./debugUtils";

/**
 * Reads a file as a base64-encoded Data URL.
 *
 * @param {File|Blob} file - The file or blob to convert.
 * @param {function(string):void} [cb] - Optional callback to receive the base64 string.
 * @returns {Promise<string>|void} If no callback is given, returns a Promise that resolves to the base64 string.
 *
 * @example
 * fileToBase64(file, (base64) => console.log(base64));
 *
 * // Promise usage:
 * fileToBase64(file).then(base64 => console.log(base64));
 */
export function fileToBase64(file, cb) {
	if (!file) {
		const errMsg = "fileUtils - fileToBase64: file is required";
		if (cb) cb(null, errMsg);
		else return Promise.reject(new Error(errMsg));
	}
	const reader = new FileReader();

	if (cb) {
		reader.onload = () => cb(reader.result);
		reader.onerror = (error) => {
			// console.error("fileToBase64 error:", error);
			errorLog(2, "[fileUtils][FLOW] fileToBase64 failed", { error: error });
			cb(null, error);
		};
		reader.readAsDataURL(file);
	} else {
		// Return a Promise if no callback
		return new Promise((resolve, reject) => {
			reader.onload = () => resolve(reader.result);
			reader.onerror = (error) => {
				// console.error("fileToBase64 error:", error);
				errorLog(2, "[fileUtils][FLOW] fileToBase64 failed", { error: error });
				reject(error);
			};
			reader.readAsDataURL(file);
		});
	}
}

/**
 * Triggers a download of the provided data as a JSON file.
 *
 * @param {Object|Array} data - The data to export as JSON.
 * @param {string} [name="data"] - The base filename (without extension).
 *
 * @example
 * exportJSON({ foo: "bar" }, "myData");
 */
export const exportJSON = (data, name = "data") => {
	// // Data-URI + encodeURIComponent caused RangeError with large strings (e.g., embedded photos)
	// // Added pretty-printing to JSON  (JSON.stringify(data, null, 2))
	// const jsonString = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(data, null, 2))}`;
	// const link = document.createElement("a");
	// link.href = jsonString;
	// link.download = `${name}_${getDateTimeString()}.json`;

	// // Appends the anchor to the DOM and removes it (improves cross-browser compatibility).
	// document.body.appendChild(link); // Needed for Firefox compatibility
	// link.click();
	// document.body.removeChild(link);

	// debugLog(2, "fileUtils - exportJSON", { filename: link.download });

	// [Blob-based download (handles very large payloads safely)
	try {
		const pretty = typeof data === "string" ? data : JSON.stringify(data, null, 2);
		const blob = new Blob([pretty], { type: "application/json;charset=utf-8" });
		const url = URL.createObjectURL(blob);

		const a = document.createElement("a");
		a.href = url;
		a.download = `${name}_${getDateTimeString()}.json`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);

		// Release memory
		setTimeout(() => URL.revokeObjectURL(url), 0);

		debugLog(2, "[fileUtils][FLOW] exportJSON blob created", {
			filename: a.download,
			size: blob.size,
		});
	} catch (err) {
		errorLog(2, "[fileUtils][FLOW] exportJSON failed", { error: err?.message });
		throw err;
	}
};

/**
 * Downloads the provided data as an Excel (XLSX) or CSV file using SheetJS (XLSX).
 *
 * @param {Object|Array} data - The data to export. If exporting XLSX, data should be an object with keys as sheet names and values as arrays of row objects. For CSV, provide an array of row objects.
 * @param {string} [fileFormat="xlsx"] - The file format to export: "xlsx" (default) or "csv".
 * @param {string} [filename="DataDump"] - The base filename (without extension).
 *
 * In XLSX mode, data should be an object where each key is a sheet name;
 * values are arrays of objects.
 * In CSV mode, data should be an array of objects.
 *
 * @example
 * // XLSX with multiple sheets:
 * exportSpreadsheet({ Sheet1: [{ a: 1 }], Sheet2: [{ b: 2 }] });
 * // CSV:
 * exportSpreadsheet([{ a: 1 }, { a: 2 }], "csv", "ExportedData");
 */
export const exportSpreadsheet = (data, fileFormat = "xlsx", filename = "DataDump") => {
	const fullFilename = `${filename}_${getDateTimeString()}`;
	const workbook = XLSX.utils.book_new();

	if (fileFormat.trim().toLowerCase() === "xlsx") {
		// Assume data is an object: { sheetName1: rowsArray1, sheetName2: rowsArray2, ... }
		for (const key in data) {
			if (Object.prototype.hasOwnProperty.call(data, key)) {
				const worksheet = XLSX.utils.json_to_sheet(data[key]);
				XLSX.utils.book_append_sheet(workbook, worksheet, key);
			}
		}
		// let buffer = XLSX.write(workbook, { bookType: "xlsx", type: "buffer" });
		// XLSX.write(workbook, { bookType: "xlsx", type: "binary" });
		XLSX.writeFile(workbook, `${fullFilename}.xlsx`);
	} else {
		// Assume data is a single array for CSV export
		const worksheet = XLSX.utils.json_to_sheet(data);
		XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
		XLSX.writeFile(workbook, `${fullFilename}.csv`, { forceQuotes: true });
	}
};

// TODO: Add more file utilities as needed.
